/**
 * Eén copilootbeurt streamend versturen (SSE over POST).
 *
 * Bewust hetzelfde patroon als `useSparStream`: een kale `fetch` in
 * plaats van `EventSource`, want die laatste kan geen
 * Authorization-header en geen POST-body meesturen. De server
 * persisteert beide beurten; deze hook schrijft ze na afloop in de
 * draad-query zodat het venster en een refresh dezelfde waarheid tonen.
 *
 * Wat hier anders is dan bij het sparvenster: de knop (`fable` /
 * `casetekst`) gaat mee, en een geweigerde beurt (vol maandplafond)
 * levert een leesbare melding in plaats van een stille storing.
 */
import { useCallback, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { huidigAccessToken } from "@/lib/auth-store";

import {
  type CopilootBericht,
  type CopilootDraad,
  type DierveldVoorstel,
  type ModelKnop,
  copilootQueryKey,
} from "./copilootApi";
import type { KijkContext } from "./kijktMee";

interface VerstuurOpties {
  knop?: ModelKnop;
  /** Stel de laatste, nog onbeantwoorde vraag opnieuw. */
  opnieuw?: boolean;
  /** Laat de copiloot verder schrijven waar hij stopte. */
  vervolg?: boolean;
  /** `dierveld`: een voorstel om een dierveld te zetten (klantpagina). */
  onKlaar?: (bericht: CopilootBericht, dierveld: DierveldVoorstel | null) => void;
  onFout?: (melding: string | null) => void;
}

/** Wat de server in een `fout`-event meestuurt als er niets meer kan. */
const ALGEMENE_FOUT =
  "De copiloot kon niet antwoorden. Probeer het zo opnieuw.";

export function useCopilootStream(context: KijkContext) {
  const qc = useQueryClient();
  const [bezig, setBezig] = useState(false);
  const [streamTekst, setStreamTekst] = useState("");
  // Voorkomt dubbel versturen terwijl een stream loopt.
  const bezigRef = useRef(false);

  const verstuur = useCallback(
    async (tekst: string, opts?: VerstuurOpties) => {
      if (bezigRef.current) return;
      bezigRef.current = true;
      setBezig(true);
      setStreamTekst("");

      // Toon de eigen vraag direct; de server bewaart hem vóór het
      // streamen en een refetch reconcilieert het tijdelijke id.
      if (!opts?.opnieuw && !opts?.vervolg) {
        qc.setQueryData<CopilootDraad>(copilootQueryKey(context), (oud) =>
          oud
            ? {
                ...oud,
                berichten: [
                  ...oud.berichten,
                  {
                    id: `lokaal-${Date.now()}`,
                    rol: "gebruiker",
                    inhoud: tekst,
                    concept_tekst: null,
                    concept_onderwerp: null,
                    afgekapt: false,
                    vervolg: false,
                    kennisbronnen: [],
                    productbronnen: [],
                    naslagbronnen: [],
                    created_at: new Date().toISOString(),
                  },
                ],
              }
            : oud,
        );
      }

      let klaarBericht: CopilootBericht | null = null;
      let dierveld: DierveldVoorstel | null = null;
      let foutMelding: string | null = null;
      let foutGezien = false;

      try {
        const token = huidigAccessToken();
        const r = await fetch(
          "/api/werkplek/copiloot/gesprek/bericht",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              tekst: opts?.opnieuw || opts?.vervolg ? null : tekst,
              model_knop: opts?.knop ?? null,
              opnieuw: opts?.opnieuw ?? false,
              vervolg: opts?.vervolg ?? false,
              context: { soort: context.soort, id: context.id },
            }),
          },
        );
        if (!r.ok || !r.body) {
          // Een geweigerde beurt (429 bij een vol maandplafond, 400 bij
          // een zaak zonder mail) draagt zijn eigen uitleg. Die tonen we
          // letterlijk: nooit "er ging iets mis" over een reden die de
          // server netjes heeft opgeschreven.
          let melding: string | null = null;
          try {
            const body = (await r.json()) as { detail?: string };
            melding = body?.detail ?? null;
          } catch {
            melding = null;
          }
          throw Object.assign(new Error("copiloot-stream"), { melding });
        }

        const reader = r.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          // SSE: events gescheiden door een lege regel.
          const blokken = buffer.split("\n\n");
          buffer = blokken.pop() ?? "";
          for (const blok of blokken) {
            const regel = blok.split("\n").find((l) => l.startsWith("data: "));
            if (!regel) continue;
            const event = JSON.parse(regel.slice(6)) as
              | { type: "delta"; tekst: string }
              | { type: "herkansing" }
              | {
                  type: "klaar";
                  bericht: CopilootBericht;
                  dierveld_voorstel?: DierveldVoorstel | null;
                }
              | { type: "fout"; melding: string };
            if (event.type === "delta") {
              setStreamTekst((prev) => prev + event.tekst);
            } else if (event.type === "herkansing") {
              // Een afgekapt antwoord wordt server-side opnieuw
              // gegenereerd; de al gestreamde tekst is waardeloos.
              setStreamTekst("");
            } else if (event.type === "klaar") {
              klaarBericht = event.bericht;
              dierveld = event.dierveld_voorstel ?? null;
            } else {
              foutGezien = true;
              foutMelding = event.melding;
            }
          }
        }
        if (klaarBericht == null && !foutGezien) {
          // Stream brak af zonder klaar- of fout-event.
          foutGezien = true;
        }
      } catch (fout) {
        foutGezien = true;
        const eigen = (fout as { melding?: string | null })?.melding;
        foutMelding = eigen ?? null;
      } finally {
        bezigRef.current = false;
        setBezig(false);
        setStreamTekst("");
      }

      if (klaarBericht != null) {
        const bericht = klaarBericht;
        qc.setQueryData<CopilootDraad>(copilootQueryKey(context), (oud) =>
          oud ? { ...oud, berichten: [...oud.berichten, bericht] } : oud,
        );
        if (opts?.vervolg) {
          // Bij een vervolg heeft de server óók een instructie-beurt
          // vastgelegd en de toelichting van de afgekapte beurt
          // bijgewerkt. Alleen een verse ophaal toont die waarheid.
          void qc.invalidateQueries({ queryKey: copilootQueryKey(context) });
        } else {
          // De kostenteller in de kop is na elke beurt verouderd.
          void qc.invalidateQueries({ queryKey: copilootQueryKey(context) });
        }
        opts?.onKlaar?.(bericht, dierveld);
      } else if (foutGezien) {
        // De vraag is server-side al bewaard; haal de draad opnieuw op
        // zodat het venster en de server dezelfde stand tonen.
        void qc.invalidateQueries({ queryKey: copilootQueryKey(context) });
        opts?.onFout?.(foutMelding ?? ALGEMENE_FOUT);
      }
    },
    [qc, context],
  );

  return { bezig, streamTekst, verstuur };
}
